import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useIsNarrow } from "@/components/shell/responsive-app-shell";
import type { FeedItem } from "@/lib/api/connections";
import {
	extensionAmbientContext,
	isAttentionItemRelevant,
	type LobuLensContext,
} from "@/lib/lens-context";
import { cn } from "@/lib/utils";
import {
	AttentionBulkBar,
	type TimelineItem,
	useAttentionInbox,
} from "./attention-inbox";
import { AttentionList, AttentionRow } from "./attention-row";
import { PageConnectionRows, usePageFeeds } from "./page-connections";

declare global {
	interface Window {
		__lobuAmbientContext?: LobuLensContext;
	}
}

function initialAmbientContext(): LobuLensContext | undefined {
	if (window.__lobuAmbientContext) return window.__lobuAmbientContext;
	// Chromium exposes the direct extension parent before the message handshake.
	// Start with an unknown page so the first render never flashes the full inbox.
	if (
		window.parent !== window &&
		/^chrome-extension:\/\/[a-p]{32}$/.test(
			window.location.ancestorOrigins?.[0] ?? "",
		)
	) {
		return {
			kind: "browser",
			url: null,
			title: null,
			applicationName: "Chrome",
		};
	}
	return undefined;
}

/** Native publishes before the SPA loads and again when the external context changes. */
export function useAmbientContext() {
	const [context, setContext] = useState(initialAmbientContext);
	useEffect(() => {
		const update = () => setContext(initialAmbientContext());
		window.addEventListener("owletto:ambient-context", update);
		update();
		const receive = (event: MessageEvent) => {
			const next = extensionAmbientContext(event, window.parent, window);
			if (next) setContext(next);
		};
		window.addEventListener("message", receive);
		if (window.parent !== window)
			window.parent.postMessage({ owletto: "ambient-context-request" }, "*");
		return () => {
			window.removeEventListener("owletto:ambient-context", update);
			window.removeEventListener("message", receive);
		};
	}, []);
	return context;
}

export type PageAttentionRow = { item: TimelineItem; onPage: boolean };

/**
 * The page in front (Chrome side panel, Mac app): its syncable feeds, and the
 * notifications about it leading the inbox. An app context never matches.
 */
export function usePageAttention(
	organizationId: string | null,
	items: TimelineItem[],
) {
	const context = useAmbientContext();
	const feeds = usePageFeeds(organizationId, context);
	const rows = useMemo(
		(): PageAttentionRow[] =>
			items
				.map((item) => ({
					item,
					onPage: context ? isAttentionItemRelevant(item, context) : false,
				}))
				.sort(
					(a, b) =>
						Number(b.onPage) - Number(a.onPage) || b.item.at - a.item.at,
				),
		[items, context],
	);
	const pageRows = useMemo(() => rows.filter(({ onPage }) => onPage), [rows]);
	return { feeds, rows, pageRows };
}

/** Notifications with the ones about the page in front tagged. */
export function PageAttentionList({
	owner,
	rows,
}: {
	owner: string;
	rows: PageAttentionRow[];
}) {
	return (
		<ul className="space-y-1">
			{rows.map(({ item, onPage }) => (
				<li key={item.id}>
					<AttentionRow
						owner={owner}
						item={item}
						tone={onPage ? "relevant" : "default"}
						tag={onPage ? "This page" : undefined}
					/>
				</li>
			))}
		</ul>
	);
}

/** The page's own rows above a stream: its connections, then its notifications. */
export function PagePinnedRows({
	owner,
	organizationId,
	feeds,
	rows,
}: {
	owner: string;
	organizationId: string | null;
	feeds: FeedItem[];
	rows: PageAttentionRow[];
}) {
	return (
		<>
			<PageConnectionRows organizationId={organizationId} feeds={feeds} />
			{rows.length > 0 ? <PageAttentionList owner={owner} rows={rows} /> : null}
		</>
	);
}

/** Lobu Lens (the picture-in-picture window): notifications for the page in
 *  front, with an explicit switch to all of them. */
export function ContextAttention({
	owner,
	context,
	emptyState,
	onOpenItem,
	compact = false,
}: {
	owner: string;
	context: LobuLensContext | undefined;
	emptyState?: ReactNode;
	onOpenItem?: (item: TimelineItem) => void;
	compact?: boolean;
}) {
	const [showAll, setShowAll] = useState(false);
	const isNarrow = useIsNarrow();
	const { attentionItems } = useAttentionInbox(owner);
	const scoped = context !== undefined && !showAll;
	const itemFilter = scoped
		? (item: { browserUrl?: string | null }) =>
				isAttentionItemRelevant(item, context)
		: undefined;
	const count = itemFilter
		? attentionItems.filter(itemFilter).length
		: attentionItems.length;
	return (
		<>
			<div
				className={cn(
					"sticky top-0 z-20 flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 bg-background pt-1",
					isNarrow ? "justify-center" : "justify-between",
				)}
			>
				{context ? (
					<fieldset
						className="flex items-center gap-2"
						aria-label="Notification scope"
					>
						<button
							type="button"
							aria-pressed={!showAll}
							onClick={() => setShowAll(false)}
							className="rounded-md px-2.5 py-1 text-xs aria-pressed:bg-muted"
						>
							Relevant here{scoped ? ` (${count})` : ""}
						</button>
						<button
							type="button"
							aria-pressed={showAll}
							onClick={() => setShowAll(true)}
							className="rounded-md px-2.5 py-1 text-xs aria-pressed:bg-muted"
						>
							All notifications
						</button>
					</fieldset>
				) : null}
				{!scoped && !compact && <AttentionBulkBar owner={owner} />}
			</div>
			<AttentionList
				owner={owner}
				itemFilter={itemFilter}
				onOpenItem={onOpenItem}
				rowAction={compact ? "none" : "open"}
				ariaLabel={scoped ? "Relevant here" : "Needs attention"}
				tone={scoped ? "relevant" : "default"}
				emptyState={
					scoped ? (
						<p className="px-3 py-6 text-center text-xs text-muted-foreground">
							No notifications linked to this context.
						</p>
					) : (
						emptyState
					)
				}
			/>
		</>
	);
}
